// =============================================================================
// src/features/explore/explore-vote-constants.ts — Tabelas estaticas dos modos
//   de visualizacao de votos por candidato na tela "Explorar".
//
// Inspiracao de COMPORTAMENTO: datapedia.info/candidato/mapa-estadual (4 modos +
//   filtro de candidato). A IDENTIDADE VISUAL e a do sistema "Political Observatory"
//   (DESIGN.md): a bolha NAO usa o teal #009688 do Datapedia — a cor vem do PARTIDO
//   (vocabulario de dado, ver PARTY_COLORS em ./constants); azul institucional segue
//   sendo o unico acento de UI (segmento ativo do seletor).
//
// SRP: so literais estaticos (tipos, lista de modos, raios, tempos de animacao,
//   parametros do campo de calor). Sem DOM, sem Leaflet, sem regra de negocio. A
//   matematica de cor/derivacao vive em ./explore-vote-data.ts.
// =============================================================================

/**
 * Modo do seletor "Tipo de Visualização". `'nenhum'` = overlay desligado (mantem o
 * choropleto/mapa limpo atual). Os 4 ativos espelham o Datapedia: contagem absoluta,
 * percentual, colocacao do candidato e campo de calor ponderado por votos.
 */
export type ExploreVoteViz = 'nenhum' | 'absolutos' | 'percentual' | 'ranking' | 'calor';

/** Botao do seletor segmentado: modo + rotulo + icone Tabler. */
export interface VoteVizModeMeta {
  readonly value: Exclude<ExploreVoteViz, 'nenhum'>;
  readonly label: string;
  readonly icon: string;
}

/** Os 4 modos ativos, na ordem do segmentado (paridade de ordem com o Datapedia). */
export const VOTE_VIZ_MODES: readonly VoteVizModeMeta[] = [
  { value: 'absolutos',  label: 'Votos absolutos', icon: 'ti-circles' },
  { value: 'percentual', label: 'Percentual',      icon: 'ti-percentage' },
  { value: 'ranking',    label: 'Melhor ranking',  icon: 'ti-medal-2' },
  { value: 'calor',      label: 'Mapa de calor',   icon: 'ti-flame' },
];

/** Raio (px) base da bolha — usado fixo em percentual/ranking (cabe "45,0%"/"1º"). */
export const BUBBLE_BASE_RADIUS = 16;
/** Raio (px) maximo da bolha no modo "absolutos" (extremo da escala sqrt). */
export const BUBBLE_MAX_RADIUS = 38;
/** Anel branco da bolha (disciplina 0.5px do sistema usa branco translucido). */
export const BUBBLE_STROKE = 'rgba(255,255,255,0.92)';
/** Espessura (px) do anel da bolha (Datapedia usa 2px). */
export const BUBBLE_STROKE_WIDTH = 2;
/** Opacidade de preenchimento da bolha em repouso (deixa a cor do partido legivel). */
export const BUBBLE_FILL_OPACITY = 0.92;

/** Atraso (ms) por indice na entrada escalonada das bolhas. */
export const STAGGER_MS = 22;
/** Teto (ms) do atraso acumulado — alem disso todas entram juntas (nao arrasta a UI). */
export const STAGGER_CAP_MS = 620;
/** Duracao (ms) do count-up dos rotulos numericos (absolutos/percentual). */
export const COUNTUP_MS = 620;

/** Raio (px) maximo do circulo translucido do campo de calor (peso 1) — grande p/ fundir. */
export const HEAT_MAX_RADIUS = 64;
/** Raio (px) minimo do circulo de calor (peso 0 — ainda forma campo continuo). */
export const HEAT_MIN_RADIUS = 22;
/** Opacidade minima do circulo de calor (peso 0 — transparente onde baixo). */
export const HEAT_MIN_OPACITY = 0.06;
/** Opacidade maxima do circulo de calor (peso 1 — nucleo quente). */
export const HEAT_MAX_OPACITY = 0.5;

/** Clay-wash do sistema (canvas/parchment) — ponta fria da rampa de calor. */
export const CLAY_WASH = '#F1EFE8';
/** Ink do sistema (near-black quente) — usado p/ "esquentar" a ponta alta do calor. */
export const INK = '#1F1F1F';
